/**
 * BCV rate resolution (PROMPT §5.1, §2.3.1, §2.3.8) — pure & testable.
 *
 * The cron stores each observed rate under the Caracas date it is published
 * (a business day). `resolveTasa` returns the exact rate for a date, or the
 * nearest PRIOR one. This naturally implements the weekend rule: BCV publishes
 * on Friday a rate (value-dated Monday) and does not publish Sat/Sun, so a
 * Saturday or Sunday operation resolves to Friday's published rate — i.e. the
 * rate that governs Sat/Sun/Mon (§2.3.1).
 */
import { monthKey } from './dates.js';
import { round4, safeDiv } from './money.js';
import type { TasaPoint } from './types.js';

/**
 * Exact rate for `fecha`, else the nearest one strictly/loosely before it
 * (`fecha <= target`, most recent). Returns null if the series has nothing on
 * or before the target date. Input series need not be sorted.
 */
export function resolveTasa(series: readonly TasaPoint[], fecha: string): TasaPoint | null {
  let best: TasaPoint | null = null;
  for (const p of series) {
    if (p.fecha <= fecha && (best === null || p.fecha > best.fecha)) {
      best = p;
    }
  }
  return best;
}

/** Just the numeric rate for a date (nearest-previous fallback), or null. */
export function tasaByDate(series: readonly TasaPoint[], fecha: string): number | null {
  return resolveTasa(series, fecha)?.tasa ?? null;
}

/** Most recent rate in the series (max fecha). */
export function latestTasa(series: readonly TasaPoint[]): TasaPoint | null {
  let best: TasaPoint | null = null;
  for (const p of series) {
    if (best === null || p.fecha > best.fecha) best = p;
  }
  return best;
}

/**
 * Average rate over a calendar month (§2.3.8). Used for aggregated USD so we
 * never anchor a month's USD to a single day's rate.
 */
export function tasaPromedioMes(
  series: readonly TasaPoint[],
  anio: number,
  mes: number,
): number | null {
  const key = `${String(anio).padStart(4, '0')}-${String(mes).padStart(2, '0')}`;
  let sum = 0;
  let n = 0;
  for (const p of series) {
    if (monthKey(p.fecha) === key) {
      sum += p.tasa;
      n += 1;
    }
  }
  const avg = safeDiv(sum, n);
  return avg == null ? null : round4(avg);
}

/**
 * Parse the BCV's Venezuelan number format "1.234,56" → 1234.56.
 * Thousands separator '.', decimal separator ','. Robust to stray spaces and
 * currency glyphs. Returns null if no numeric content is found.
 */
export function parseVeNumber(raw: string): number | null {
  if (raw == null) return null;
  const cleaned = raw.replace(/\s+/g, '').replace(/[^0-9.,-]/g, ''); // drop "Bs", "$", etc.
  if (cleaned === '' || cleaned === '-') return null;
  // Remove thousands '.', then turn decimal ',' into '.'.
  const normalized = cleaned.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}
